import React, { useState, useEffect, useCallback } from 'react';
import { FiFilter, FiSearch, FiX } from 'react-icons/fi';

const InventoryFilters = ({ filters, onFiltersChange, categories = [] }) => {
  const [searchValue, setSearchValue] = useState(filters.search || '');

  const handleFilterChange = useCallback((key, value) => {
    const newFilters = { ...filters, [key]: value };
    onFiltersChange(newFilters);
  }, [filters, onFiltersChange]);

  // Sync searchValue with filters.search when filters change externally
  useEffect(() => {
    setSearchValue(filters.search || '');
  }, [filters.search]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        handleFilterChange('search', searchValue);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue, filters.search, handleFilterChange]);

  const handlePriceRangeChange = (type, value) => {
    onFiltersChange({
      ...filters,
      priceRange: {
        ...filters.priceRange,
        [type]: value
      }
    });
  };

  const handleStockRangeChange = (type, value) => {
    onFiltersChange({
      ...filters,
      stockRange: {
        ...filters.stockRange,
        [type]: value
      }
    });
  };

  const resetFilters = () => {
    setSearchValue('');
    onFiltersChange({
      search: '',
      category: '',
      status: '',
      priceRange: { min: '', max: '' },
      stockRange: { min: '', max: '' }
    });
  };

  const hasActiveFilters = () => {
    return filters.search || 
           filters.category || 
           filters.status || 
           filters.priceRange.min || 
           filters.priceRange.max ||
           filters.stockRange.min || 
           filters.stockRange.max;
  };

  return (
    <div className="inventory-filters">
      <div className="filters-header">
        <h3><FiFilter /> Filters</h3>
        {hasActiveFilters() && (
          <button onClick={resetFilters} className="btn btn-sm btn-secondary">
            <FiX /> Clear All
          </button>
        )}
      </div>

      <div className="filters-content">
        <div className="filter-group">
          <label>Search</label>
          <div className="search-input">
            <FiSearch />
            <input
              type="text"
              placeholder="Search by name, ID, HSN, or description..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id || category} value={category.name || category}>
                  {category.name || category}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
              <option value="discontinued">Discontinued</option>
            </select>
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Price Range (₹)</label>
            <div className="range-inputs">
              <input
                type="number"
                placeholder="Min"
                value={filters.priceRange.min}
                onChange={(e) => handlePriceRangeChange('min', e.target.value)}
                step="0.01"
                min="0"
              />
              <span>to</span>
              <input
                type="number"
                placeholder="Max"
                value={filters.priceRange.max}
                onChange={(e) => handlePriceRangeChange('max', e.target.value)}
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div className="filter-group">
            <label>Stock Range</label>
            <div className="range-inputs">
              <input
                type="number"
                placeholder="Min"
                value={filters.stockRange.min}
                onChange={(e) => handleStockRangeChange('min', e.target.value)}
                min="0"
              />
              <span>to</span>
              <input
                type="number"
                placeholder="Max"
                value={filters.stockRange.max}
                onChange={(e) => handleStockRangeChange('max', e.target.value)}
                min="0"
              />
            </div>
          </div>
        </div>

        <div className="filter-actions">
          <button 
            className="btn btn-primary btn-sm"
            onClick={() => {
              // Trigger a re-fetch by calling onFiltersChange with current filters
              onFiltersChange({ ...filters });
            }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryFilters;
